import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Params, RouterModule } from '@angular/router';

export type UnderlineButtonSize = 'md' | 'sm';

/**
 * The design system's "Button Ghost": an underlined label with an optional leading SVG icon.
 *
 * It exists so the icon-to-label distance is fixed in one place — every page that hand-rolled
 * this pairing picked its own gap. The label is projected, so callers keep translating their
 * own copy.
 *
 * Give it a `link` when the control navigates and it renders an anchor; leave it off and it
 * renders a button that emits `pressed`. Both get the same treatment, so a navigating control
 * does not have to be rebuilt by hand to keep its href.
 */
@Component({
  selector: 'app-underline-button',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, RouterModule],
  templateUrl: './underline-button.component.html',
  styleUrls: ['./underline-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnderlineButtonComponent {
  /**
   * Name of a leading icon registered with `MatIconRegistry`, e.g. `ph-regular-camera`. Omitted
   * renders a label-only button.
   *
   * Inlined by `mat-icon`, so the glyph takes the label's colour — the icon and the text it
   * belongs to cannot drift apart.
   */
  @Input() svgIcon?: string;
  @Input() size: UnderlineButtonSize = 'md';
  @Input() disabled: boolean = false;
  /** Route to navigate to. Set, the control renders as an anchor instead of a button. */
  @Input() link?: string | unknown[];
  @Input() queryParams?: Params;

  @Output() pressed = new EventEmitter<void>();
}
